import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Camera, Filter, Loader2, ArrowRight, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/SEOHead';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: 'all', label: 'All' },
  { value: 'resin', label: 'Resin Art' },
  { value: 'jewelry', label: 'Jewelry' },
  { value: 'textiles', label: 'Textiles' },
  { value: 'woodwork', label: 'Woodwork' },
  { value: 'ceramics', label: 'Ceramics' },
  { value: 'home-decor', label: 'Home Decor' },
  { value: 'fine-art', label: 'Fine Art' },
  { value: 'events', label: 'Events' },
];

export default function GalleryPage() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        // First try to seed the gallery
        await axios.post(`${API}/gallery/seed`);
        // Then fetch all items
        const response = await axios.get(`${API}/gallery`);
        setItems(response.data);
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGallery();
  }, []);

  const filteredItems = activeCategory === 'all' 
    ? items 
    : items.filter(item => item.category === activeCategory);

  return (
    <div className="page-transition" data-testid="gallery-page">
      <SEOHead page="gallery" />
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Camera className="w-4 h-4" />
              <span>Our Gallery</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-serif font-semibold text-foreground mb-6">
              Artisan Showcase
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Explore the beautiful handcrafted work from our talented artisan community. 
              Each piece tells a story of passion, skill, and creativity.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
              <Link to="/contact">
                <Button size="lg" className="rounded-full" data-testid="gallery-vendor-cta">
                  Become a Vendor
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="rounded-full" data-testid="gallery-event-cta">
                  Book an Event
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            
            {/* Vendor Community Link */}
            <div className="mt-10 pt-8 border-t border-border/50">
              <p className="text-muted-foreground mb-4">
                Discover other vendors in the AMS Community
              </p>
              <a 
                href="https://madeartisanmarkets.com/community" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button variant="secondary" className="rounded-full" data-testid="gallery-community-cta">
                  <Users className="w-4 h-4 mr-2" />
                  Explore Vendor Community
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 border-b sticky top-20 bg-background/95 backdrop-blur-sm z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-serif font-semibold text-foreground mb-4 text-center">
            Browse our Artist and Event Gallery
          </h2>
          <div className="flex items-center justify-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <Filter className="w-4 h-4 text-muted-foreground flex-shrink-0 mr-2" />
            {categories.map((category) => (
              <Button
                key={category.value}
                variant={activeCategory === category.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveCategory(category.value)}
                className="rounded-full whitespace-nowrap"
                data-testid={`gallery-filter-${category.value}`}
              >
                {category.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground text-lg">No items found in this category.</p>
            </div>
          ) : (
            <div className="masonry-grid">
              {filteredItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="masonry-item group"
                  data-testid={`gallery-item-${item.id}`}
                >
                  <div className="relative overflow-hidden rounded-2xl bg-muted">
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      onError={(e) => {
                        // Some legacy items reference an ephemeral upload path that
                        // no longer exists. Swap in an on-brand placeholder so the
                        // public gallery never renders broken-image icons.
                        if (!e.target.dataset.fallback) {
                          e.target.dataset.fallback = '1';
                          e.target.src =
                            'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=1200&q=80&auto=format&fit=crop';
                        }
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <h3 className="text-white font-serif font-semibold text-lg mb-1">
                          {item.title}
                        </h3>
                        {item.artisan_name && (
                          <p className="text-white/80 text-sm">by {item.artisan_name}</p>
                        )}
                        {item.description && (
                          <p className="text-white/60 text-sm mt-2">{item.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Vendor Community Section */}
      <section className="py-16 md:py-24 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Users className="w-4 h-4" />
              <span>Vendor Community</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-4">
              Meet Our Artisan Family
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
              Discover other vendors in the AMS community and find out what events they are attending.
            </p>
            <a 
              href="https://madeartisanmarkets.com/community" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button 
                size="lg" 
                className="bg-white text-primary hover:bg-white/90 rounded-full px-8"
                data-testid="vendor-community-btn"
              >
                Explore Vendor Community
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
